import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { isSupportedLocale, type SupportedLocale } from "@/i18n";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  fr: "🇫🇷",
  en: "🇬🇧",
  es: "🇪🇸",
  nl: "🇳🇱",
};

type FieldCustomizationRowProps = {
  fieldKey: string;
  adminLabel: string;
  rowLocale: SupportedLocale;
  supportedLocales: readonly SupportedLocale[];
  value: string;
  isSaving: boolean;
  onLocaleChange: (locale: SupportedLocale) => void;
  onValueChange: (value: string) => void;
};

export function FieldCustomizationRow({
  fieldKey,
  adminLabel,
  rowLocale,
  supportedLocales,
  value,
  isSaving,
  onLocaleChange,
  onValueChange,
}: FieldCustomizationRowProps) {
  const { t } = useTranslation();

  return (
    <div
      key={fieldKey}
      className={cn(
        "flex flex-col gap-2 w-full",
        "md:flex-row md:items-center md:gap-3",
      )}
    >
      <Input
        readOnly
        disabled
        value={adminLabel}
        className="md:flex-1 bg-slate-100 text-slate-700"
      />
      <ChevronRight
        className={cn(
          "shrink-0 w-5 h-5 text-brand-600 self-center",
          "md:rotate-0 rotate-90",
        )}
      />
      <div
        className={cn(
          "flex h-9 min-h-9 w-full overflow-hidden rounded-md border border-input bg-white text-sm shadow-sm transition-[color,box-shadow]",
          "focus-within:outline-none focus-within:ring-1 focus-within:ring-ring",
          "md:flex-1",
          isSaving && "pointer-events-none opacity-50",
        )}
      >
        <Select
          value={rowLocale}
          disabled={isSaving}
          onValueChange={(v) => {
            if (!isSupportedLocale(v)) return;
            onLocaleChange(v);
          }}
        >
          <SelectTrigger
            aria-label=            {t("settings.general.fieldCustomization.selectEditLanguage")}
            className={cn(
              "h-9 shrink-0 gap-1.5 rounded-none border-0 border-r border-input/80 bg-slate-50/60 px-2.5 py-0 shadow-none",
              "w-auto min-w-[3.25rem] justify-center focus:ring-0 focus:ring-offset-0",
              "data-[state=open]:bg-slate-100/80",
              "[&>svg:last-child]:hidden",
            )}
          >
            <span className="text-base leading-none" aria-hidden>
              {LOCALE_FLAGS[rowLocale]}
            </span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden
            />
          </SelectTrigger>
          <SelectContent align="start">
            {supportedLocales.map((loc) => (
              <SelectItem key={loc} value={loc}>
                {LOCALE_FLAGS[loc]} {loc.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          type="text"
          placeholder="XXX"
          disabled={isSaving}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent px-3 py-1 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}
